import { appConfirm } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
// PatientFeedback.js
// =============================================================================
// Surveys > Feedback — full-screen module opened from the Head Office
// dashboard's new "Surveys" row (see HeadOffice.js: DASHBOARD_GROUPS +
// SurveysCard + ComplianceFullPage's `patientFeedback` case).
//
// Self-contained: fetches its own token from localStorage, talks to its own
// backend module (feedback_routes.js), and has no dependency on any other
// component in this repo.
//
// Four tabs, right to left as designed:
//   Poster design    — A4 wall poster: pharmacy name, three resizable text
//                       blocks and a QR code, each positioned/coloured/sized.
//                       Preview renders a real PDF; Cascade emails one PDF
//                       per branch (branch name + branch-specific QR) to
//                       that branch's contact address.
//   Webpage design    — the page the QR opens: position the pharmacy name,
//                       toggle rating / comments / complaint, live preview.
//   Branches           — grid of branches x response counts over a date
//                       range, with an Excel export.
//   Inbox              — incoming feedback not yet reviewed (plus a
//                       Reviewed / All view). Add a note, mark reviewed,
//                       reply by email (routes further correspondence to
//                       the manager's own configured address, not the app).
//
// The public-facing page a customer's QR scan opens lives in
// PatientFeedbackPublic.js, rendered by App.js's "?pfb=TOKEN" early return.
// =============================================================================

import React, { useState, useEffect, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import { FIELDS as POSTER_FIELDS, normalizePoster as normalizeFixedPoster, buildPosterHtml } from "./FeedbackPosterTemplate";
import FeedbackContactLayout, { CONTACT_FIELDS, DEFAULT_CONTACT_BLOCKS, contactBlock, contactHeight } from "./FeedbackContactLayout";
const API_BASE = apiUrl("");
const token = () => localStorage.getItem("token") || localStorage.getItem("authToken") || "";
async function apiPost(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`
    },
    body: JSON.stringify(body || {})
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}
async function apiPostBlob(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`
    },
    body: JSON.stringify(body || {})
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return res.blob();
}
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// Mirrors feedback_routes.js's DEFAULT_POSTER / DEFAULT_WEBPAGE exactly —
// kept here too so "Restore defaults" is instant and works offline-first,
// without a round trip. If the backend defaults ever change, update both.
const DEFAULT_POSTER = normalizeFixedPoster();
const DEFAULT_WEBPAGE = {
  contactCanvasHeight: 1100,
  contactBlocks: DEFAULT_CONTACT_BLOCKS,
  pharmacyName: {
    x: 50,
    y: 8,
    align: "center",
    fontSize: 22,
    fg: "#262626"
  },
  introText: "Tell us about your visit today",
  showRating: true,
  showComments: true,
  showComplaint: true,
  showTrustpilotInvite: false,
  trustpilotInviteText: "If you have a moment, we'd really appreciate you sharing your experience on Trustpilot.",
  ratingHelp: "Tap a star to rate your visit, from 1 (poor) to 5 (excellent).",
  commentsHelp: "Tell us what went well, or what we could do better — every comment is read by the branch manager.",
  complaintHelp: "Tick this if you'd like this treated as a formal complaint. We aim to respond within 5 working days."
};

// Mirrors feedback_routes.js's DEFAULT_ACKNOWLEDGEMENT.text / DEFAULT_REMINDER_HOURS.
const DEFAULT_ACKNOWLEDGEMENT_TEXT = "Thank you for getting in touch with us. We're sorry to hear your visit to {{pharmacyName}} didn't go as " + "it should have, and we take every complaint seriously.\n\n" + "Your complaint has been passed to the branch manager, who will look into it and respond to you directly. " + "We aim to respond within 5 working days.";
const DEFAULT_REMINDER_HOURS = 48;
const cloneDefault = obj => JSON.parse(JSON.stringify(obj));

// ---------------------------------------------------------------------------
// styles
// ---------------------------------------------------------------------------
const S = {
  wrap: {
    padding: 16,
    fontFamily: "system-ui, sans-serif"
  },
  h1: {
    fontSize: 20,
    fontWeight: 700,
    margin: "0 0 4px",
    color: "#262626"
  },
  sub: {
    fontSize: 13,
    color: "#5D5D5D",
    margin: "0 0 14px"
  },
  tabs: {
    display: "flex",
    gap: 6,
    marginBottom: 16,
    borderBottom: "1px solid #F0F0F0",
    flexWrap: "wrap"
  },
  tabBtn: active => ({
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
    borderBottom: active ? "3px solid #722ED1" : "3px solid transparent",
    background: "transparent",
    color: active ? "#262626" : "#5D5D5D"
  }),
  panelRow: {
    display: "flex",
    gap: 20,
    alignItems: "flex-start",
    flexWrap: "wrap"
  },
  card: {
    background: "#fff",
    border: "1px solid #F0F0F0",
    borderRadius: 8,
    padding: 14
  },
  label: {
    fontSize: 11.5,
    fontWeight: 700,
    color: "#434343",
    textTransform: "uppercase",
    letterSpacing: 0.3,
    display: "block",
    margin: "10px 0 4px"
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "7px 9px",
    fontSize: 13,
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    fontFamily: "inherit"
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    padding: "7px 9px",
    fontSize: 13,
    minHeight: 60,
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    fontFamily: "inherit",
    resize: "vertical"
  },
  btn: {
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    border: "none",
    borderRadius: 6,
    background: "#722ED1",
    color: "#fff",
    cursor: "pointer"
  },
  btnGhost: {
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 6,
    cursor: "pointer",
    background: "#fff",
    color: "#434343",
    border: "1px solid #D9D9D9"
  },
  btnDanger: {
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 6,
    cursor: "pointer",
    background: "#CF1322",
    color: "#fff",
    border: "none"
  },
  err: {
    fontSize: 12.5,
    color: "#A8071A",
    background: "#FFF1F0",
    border: "1px solid #FFCCC7",
    borderRadius: 6,
    padding: "7px 10px",
    marginTop: 10
  },
  ok: {
    fontSize: 12.5,
    color: "#389E0D",
    background: "#F6FFED",
    border: "1px solid #B7EB8F",
    borderRadius: 6,
    padding: "7px 10px",
    marginTop: 10
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13
  },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "2px solid #F0F0F0",
    fontSize: 11.5,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    color: "#5D5D5D"
  },
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #F5F5F5",
    verticalAlign: "top"
  }
};

// ---------------------------------------------------------------------------
// Draggable / resizable design block — percentage coordinates (0-100) of a
// fixed-aspect canvas, so the layout survives print at any DPI. Click
// selects (for the properties panel); drag on the body moves; drag on the
// small corner handle resizes.
// ---------------------------------------------------------------------------
function DesignBlock({
  block,
  canvasSize,
  onChange,
  onSelect,
  onAlign,
  selected,
  resizable = true,
  minW = 6,
  minH = 4,
  children
}) {
  const dragRef = useRef(null);
  useEffect(() => {
    function onMove(e) {
      const d = dragRef.current;
      if (!d) return;
      const dxPct = (e.clientX - d.startX) / canvasSize.w * 100;
      const dyPct = (e.clientY - d.startY) / canvasSize.h * 100;
      if (d.mode === "move") {
        const x = clamp(d.start.x + dxPct, 0, 100 - d.start.w);
        const y = clamp(d.start.y + dyPct, 0, 100 - d.start.h);
        onChange({
          ...d.start,
          x,
          y
        });
      } else {
        const w = clamp(d.start.w + dxPct, minW, 100 - d.start.x);
        const h = clamp(d.start.h + dyPct, minH, 100 - d.start.y);
        onChange({
          ...d.start,
          w,
          h
        });
      }
    }
    function onUp() {
      dragRef.current = null;
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [canvasSize, onChange, minW, minH]);
  return <div onMouseDown={e => {
    e.stopPropagation();
    onSelect && onSelect();
    dragRef.current = {
      mode: "move",
      startX: e.clientX,
      startY: e.clientY,
      start: block
    };
  }} style={{
    position: "absolute",
    left: `${block.x}%`,
    top: `${block.y}%`,
    width: `${block.w}%`,
    height: `${block.h}%`,
    cursor: "move",
    boxSizing: "border-box",
    outline: selected ? "2px dashed #722ED1" : "1px dashed #D9D9D9",
    outlineOffset: 2
  }}>
      {children}
      {onAlign && <AlignmentMenu value={block.align || "left"} onChange={onAlign} />}
      {resizable && <div onMouseDown={e => {
      e.stopPropagation();
      onSelect && onSelect();
      dragRef.current = {
        mode: "resize",
        startX: e.clientX,
        startY: e.clientY,
        start: block
      };
    }} style={{
      position: "absolute",
      right: -5,
      bottom: -5,
      width: 12,
      height: 12,
      background: "#722ED1",
      borderRadius: 3,
      cursor: "nwse-resize",
      zIndex: 5
    }} />}
    </div>;
}
function AlignmentMenu({
  value,
  onChange
}) {
  const [open, setOpen] = useState(false);
  return <div style={{
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 10
  }} onMouseDown={e => e.stopPropagation()}>
    <button type="button" aria-label="Text alignment" aria-expanded={open} title="Text alignment" onClick={e => {
      e.stopPropagation();
      setOpen(v => !v);
    }} style={{
      width: 24,
      height: 24,
      padding: 2,
      border: "1px solid #D3ADF7",
      borderRadius: 4,
      color: "#531DAB",
      background: "#fff",
      cursor: "pointer"
    }}>☰</button>
    {open && <div role="menu" style={{
      position: "absolute",
      right: 0,
      top: 26,
      minWidth: 112,
      background: "#fff",
      boxShadow: "0 2px 8px #0002",
      border: "1px solid #ddd",
      borderRadius: 4
    }}>
      {[["left", "Align left"], ["center", "Centre"], ["right", "Align right"], ["justify", "Justify"]].map(([key, label]) => <button key={key} type="button" role="menuitemradio" aria-checked={value === key} onClick={e => {
        e.stopPropagation();
        onChange(key);
        setOpen(false);
      }} style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "6px 8px",
        border: "none",
        background: value === key ? "#F9F0FF" : "#fff",
        cursor: "pointer"
      }}>{label}</button>)}
    </div>}
  </div>;
}
function ColorField({
  label,
  value,
  onChange,
  allowTransparent
}) {
  const isTransparent = value === "transparent" || !value;
  return <div>
      <label style={S.label}>{label}</label>
      <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8
    }}>
        <input type="color" value={isTransparent ? "#ffffff" : value} onChange={e => onChange(e.target.value)} disabled={allowTransparent && isTransparent} style={{
        width: 40,
        height: 30,
        padding: 0,
        border: "1px solid #D9D9D9",
        borderRadius: 4
      }} />
        {allowTransparent && <label style={{
        fontSize: 12,
        color: "#434343",
        display: "flex",
        alignItems: "center",
        gap: 4
      }}>
            <input type="checkbox" checked={isTransparent} onChange={e => onChange(e.target.checked ? "transparent" : "#ffffff")} />
            None
          </label>}
      </div>
    </div>;
}

// =============================================================================
// POSTER DESIGN TAB
// =============================================================================

function RecipientsPanel() {
  const [branches, setBranches] = useState(null);
  const [err, setErr] = useState(null);
  const [savingFor, setSavingFor] = useState(null);
  const [savingSuperFor, setSavingSuperFor] = useState(null);
  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await apiPost("/api/feedback/branches", {});
      setBranches(r.branches);
    } catch (e) {
      setErr(e.message);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const choose = async (dbName, staffId) => {
    setSavingFor(dbName);
    setBranches(rows => rows.map(b => {
      if (b.dbName !== dbName) return b;
      const m = (b.managers || []).find(x => String(x.staffId) === String(staffId));
      return {
        ...b,
        recipientStaffId: m ? m.staffId : "",
        recipientName: m ? m.name : "",
        recipientEmail: m ? m.email : ""
      };
    }));
    try {
      await apiPost("/api/feedback/branches/recipient", {
        dbName,
        staffId: staffId || null
      });
    } catch (e) {
      setErr(e.message);
    }
    setSavingFor(null);
  };

  // Superintendent email address (item i) — a plain free-text address, not
  // tied to a staff record, so it's edited inline and saved on blur rather
  // than through the managers dropdown above.
  const editSuperintendent = (dbName, email) => {
    setBranches(rows => rows.map(b => b.dbName === dbName ? {
      ...b,
      superintendentEmail: email
    } : b));
  };
  const saveSuperintendent = async (dbName, email) => {
    setSavingSuperFor(dbName);
    try {
      await apiPost("/api/feedback/branches/superintendent", {
        dbName,
        email
      });
    } catch (e) {
      setErr(e.message);
    }
    setSavingSuperFor(null);
  };
  return <div style={{
    ...S.card,
    marginTop: 16
  }}>
      <div style={{
      fontWeight: 700,
      fontSize: 14,
      marginBottom: 4
    }}>Who receives each branch's posters and complaints</div>
      <div style={{
      fontSize: 12,
      color: "#5D5D5D",
      marginBottom: 10
    }}>
        Same picker as the Action Centre — only active managers/pharmacists with an email on file are listed.
        Leave a branch blank to skip it when you cascade. The superintendent address is used only when a
        complaint's outcome is marked "Superintendent to be informed".
      </div>
      {err && <div style={S.err}>{err}</div>}
      {!branches && !err && <div style={{
      color: "#5D5D5D",
      fontSize: 13
    }}>Loading…</div>}
      {branches && <div style={{
      overflowX: "auto"
    }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Branch</th>
                <th style={S.th}>Recipient</th>
                <th style={S.th}>Superintendent email address</th>
              </tr>
            </thead>
            <tbody>
              {branches.map(b => <tr key={b.dbName}>
                  <td style={S.td}>{b.pharmacyName}</td>
                  <td style={S.td}>
                    <select style={{
                ...S.input,
                width: "auto",
                minWidth: 220
              }} value={b.recipientStaffId || ""} disabled={savingFor === b.dbName} onChange={e => choose(b.dbName, e.target.value)}>
                      <option value="">
                        {(b.managers || []).length ? "— Select recipient —" : "— No manager/pharmacist with email —"}
                      </option>
                      {(b.managers || []).map(m => <option key={m.staffId} value={m.staffId}>{m.name} ({m.roleName}) — {m.email}</option>)}
                    </select>
                  </td>
                  <td style={S.td}>
                    <input type="email" style={{
                ...S.input,
                width: "auto",
                minWidth: 220
              }} placeholder="superintendent@yourpharmacy.co.uk" value={b.superintendentEmail || ""} disabled={savingSuperFor === b.dbName} onChange={e => editSuperintendent(b.dbName, e.target.value)} onBlur={e => saveSuperintendent(b.dbName, e.target.value)} />
                  </td>
                </tr>)}
              {branches.length === 0 && <tr><td style={S.td} colSpan={3}>No branches found.</td></tr>}
            </tbody>
          </table>
        </div>}
    </div>;
}
function PosterDesignTab({
  poster,
  setPoster,
  saving,
  onSave,
  message
}) {
  const design = normalizeFixedPoster(poster);
  const [previewBranches, setPreviewBranches] = useState([]);
  const [previewBranchId, setPreviewBranchId] = useState("");
  const [previewData, setPreviewData] = useState({
    pharmacyName: "Sample Pharmacy",
    phone: "",
    qrDataUrl: ""
  });
  const [logoLoading, setLogoLoading] = useState(false);
  useEffect(() => {
    let active = true;
    apiPost("/api/feedback/branches", {}).then(r => {
      if (!active) return;
      setPreviewBranches(r.branches || []);
      setPreviewBranchId(r.branches?.[0]?.dbName || "");
    }).catch(e => {
      if (active) setLocalMsg({
        type: "err",
        text: e.message
      });
    });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    if (previewBranchId) {
      setPreviewData({
        pharmacyName: previewBranches.find(b => b.dbName === previewBranchId)?.pharmacyName || "Sample Pharmacy",
        phone: "",
        qrDataUrl: ""
      });
      apiPost("/api/feedback/poster/preview-data", {
        dbName: previewBranchId
      }).then(r => {
        if (active) setPreviewData(r);
      }).catch(e => {
        if (active) setLocalMsg({
          type: "err",
          text: e.message
        });
      });
    }
    return () => {
      active = false;
    };
  }, [previewBranchId, previewBranches]);
  const setDesign = patch => setPoster(p => ({
    ...normalizeFixedPoster(p),
    ...patch
  }));
  const updateField = (key, patch) => setPoster(p => {
    const next = normalizeFixedPoster(p);
    return {
      ...next,
      fields: {
        ...next.fields,
        [key]: {
          ...next.fields[key],
          ...patch
        }
      }
    };
  });
  const uploadLogo = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type) || file.size > 1000000) {
      setLocalMsg({
        type: "err",
        text: "Choose a PNG or JPEG logo under 1 MB."
      });
      return;
    }
    setLogoLoading(true);
    setLocalMsg(null);
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const image = new Image();
      image.src = data;
      await image.decode();
      normalizeFixedPoster({
        ...design,
        logoDataUrl: data
      });
      setDesign({
        logoDataUrl: data
      });
    } catch (_) {
      setLocalMsg({
        type: "err",
        text: "This logo could not be loaded. Please choose a valid PNG or JPEG."
      });
    }
    setLogoLoading(false);
  };
  const [previewLoading, setPreviewLoading] = useState(false);
  const [cascadeBusy, setCascadeBusy] = useState(false);
  const [cascadeResult, setCascadeResult] = useState(null);
  const [localMsg, setLocalMsg] = useState(null);

  // Branch-picker modal for "Cascade to branches" — load the branch list on
  // open, default every checkbox on, let the user narrow it down.
  const [cascadeModalOpen, setCascadeModalOpen] = useState(false);
  const [cascadeBranches, setCascadeBranches] = useState(null); // [{ dbName, pharmacyName, checked }]
  const [cascadeLoadErr, setCascadeLoadErr] = useState(null);
  const doPreview = async () => {
    setPreviewLoading(true);
    setLocalMsg(null);
    try {
      const blob = await apiPostBlob("/api/feedback/poster/preview", {
        poster: design,
        dbName: previewBranchId || undefined
      });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      setLocalMsg({
        type: "err",
        text: e.message
      });
    }
    setPreviewLoading(false);
  };
  const openCascadeModal = async () => {
    setCascadeModalOpen(true);
    setCascadeBranches(null);
    setCascadeLoadErr(null);
    try {
      const r = await apiPost("/api/feedback/branches", {});
      setCascadeBranches((r.branches || []).map(b => ({
        dbName: b.dbName,
        pharmacyName: b.pharmacyName || b.dbName,
        checked: true
      })));
    } catch (e) {
      setCascadeLoadErr(e.message);
    }
  };
  const toggleCascadeBranch = dbName => {
    setCascadeBranches(rows => rows.map(b => b.dbName === dbName ? {
      ...b,
      checked: !b.checked
    } : b));
  };
  const toggleCascadeAll = checked => {
    setCascadeBranches(rows => rows.map(b => ({
      ...b,
      checked
    })));
  };
  const allCascadeChecked = !!cascadeBranches && cascadeBranches.length > 0 && cascadeBranches.every(b => b.checked);
  const sendCascadeToSelected = async () => {
    const dbNames = (cascadeBranches || []).filter(b => b.checked).map(b => b.dbName);
    if (!dbNames.length) return;
    setCascadeBusy(true);
    setCascadeResult(null);
    setLocalMsg(null);
    try {
      await apiPost("/api/feedback/config/save", {
        poster: design
      });
      const r = await apiPost("/api/feedback/poster/cascade", {
        dbNames
      });
      setCascadeResult(r);
      setCascadeModalOpen(false);
    } catch (e) {
      setLocalMsg({
        type: "err",
        text: e.message
      });
    }
    setCascadeBusy(false);
  };
  const restoreDefaults = async () => {
    if (!(await appConfirm("Reset the poster text, font sizes, logo and telephone option to the template defaults? This can't be undone unless you saved a version you want to keep."))) return;
    setPoster(cloneDefault(DEFAULT_POSTER));
    setLocalMsg({
      type: "ok",
      text: "Restored to defaults — click Save design to keep this."
    });
  };
  return <>
    <div style={S.panelRow}>
      <div style={{
        ...S.card,
        width: 400
      }}>
        <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginBottom: 10
        }}>Fixed A4 layout. Each exported poster uses its branch's name and QR code.</div>
        <label style={S.label}>Preview branch</label>
        <select style={S.input} value={previewBranchId} onChange={e => setPreviewBranchId(e.target.value)}>
          {!previewBranches.length && <option value="">Sample Pharmacy</option>}
          {previewBranches.map(b => <option key={b.dbName} value={b.dbName}>{b.pharmacyName}</option>)}
        </select>
        <div style={{
          width: "100%",
          maxWidth: 380,
          aspectRatio: "210 / 297",
          overflow: "hidden",
          marginTop: 12,
          border: "1px solid #D9D9D9"
        }}>
          <iframe title="Fixed feedback poster preview" sandbox="" srcDoc={buildPosterHtml({
            poster: design,
            ...previewData
          })} style={{
            width: 794,
            height: 1123,
            border: 0,
            transform: "scale(0.47859)",
            transformOrigin: "top left",
            pointerEvents: "none"
          }} />
        </div>
      </div>
      <div style={{
        ...S.card,
        minWidth: 300,
        flex: 1
      }}>
        <div style={{
          fontWeight: 700,
          fontSize: 14
        }}>Poster text and font sizes</div>
        <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          margin: "6px 0 12px"
        }}>Edit the heading, introduction and footer text and font sizes. The QR caption and instructions use fixed wording; line breaks are supported.</div>
        <label style={S.label}>Shared logo (optional, all branches)</label>
        <input type="file" accept="image/png,image/jpeg" onChange={uploadLogo} disabled={logoLoading || saving || cascadeBusy} />
        {design.logoDataUrl && <button style={{
          ...S.btnGhost,
          marginLeft: 8
        }} onClick={() => setDesign({
          logoDataUrl: ""
        })}>Remove logo</button>}
        <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginTop: 6
        }}>PNG or JPEG, under 1 MB. Without a logo, the logo box is omitted.</div>
        <label style={S.label}>Automatic pharmacy name — font size</label>
        <input type="range" step="0.01" min="16" max="30" value={design.nameFontSize} onChange={e => setDesign({
          nameFontSize: Number(e.target.value)
        })} /> {design.nameFontSize} pt
        {POSTER_FIELDS.filter(([key]) => ["eyebrow", "headline", "message", "footerTitle", "footerText"].includes(key)).map(([key, label, text, size, min, max]) => <div key={key} style={{
          marginTop: 12,
          paddingTop: 10,
          borderTop: "1px solid #eee"
        }}>
          <label style={S.label}>{label}{key === "eyebrow" && <span style={{
              fontWeight: 400,
              textTransform: "none",
              marginLeft: 8
            }}>Maximum 15 characters ({design.fields[key].text.length}/15)</span>}</label>
          <textarea style={{
            ...S.textarea,
            minHeight: key === "message" ? 85 : 48
          }} value={design.fields[key].text} maxLength={key === "eyebrow" ? 15 : 1500} onChange={e => updateField(key, {
            text: e.target.value
          })} />
          <label style={{
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 4
          }}>
            Font size <input type="range" step="0.01" min={min} max={max} value={design.fields[key].fontSize} onChange={e => updateField(key, {
              fontSize: Number(e.target.value)
            })} /> {design.fields[key].fontSize} pt
          </label>
        </div>)}
        <label style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 16,
          fontSize: 13
        }}>
          <input type="checkbox" checked={design.showTelephone} onChange={e => setDesign({
            showTelephone: e.target.checked
          })} />Include the branch telephone number in the footer
        </label>
        <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginTop: 6
        }}>Uses the public telephone number saved for that branch in Webpage design. Blank numbers are omitted.</div>
        {design.showTelephone && <label style={{
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 8
        }}>
          Telephone font size <input type="range" step="0.01" min="10" max="22" value={design.phoneFontSize} onChange={e => setDesign({
            phoneFontSize: Number(e.target.value)
          })} /> {design.phoneFontSize} pt
        </label>}
        <div style={{
          display: "flex",
          gap: 8,
          marginTop: 16,
          flexWrap: "wrap",
          alignItems: "center"
        }}>
          <button style={{
            ...S.btn,
            minHeight: 36
          }} onClick={onSave} disabled={saving || logoLoading || cascadeBusy}>{saving ? "Saving…" : "Save design"}</button>
          <button style={{
            ...S.btnGhost,
            minHeight: 36
          }} onClick={doPreview} disabled={previewLoading || logoLoading || cascadeBusy}>{previewLoading ? "Rendering…" : "Preview PDF"}</button>
          <button style={{
            ...S.btnDanger,
            minHeight: 36
          }} onClick={openCascadeModal} disabled={cascadeBusy || saving || logoLoading}>{cascadeBusy ? "Sending…" : "Cascade to branches…"}</button>
          <button style={{
            ...S.btnGhost,
            minHeight: 36
          }} disabled={cascadeBusy || saving || logoLoading} onClick={() => setPoster(p => {
            const next = normalizeFixedPoster(p),
              defaults = normalizeFixedPoster();
            return {
              ...next,
              nameFontSize: defaults.nameFontSize,
              phoneFontSize: defaults.phoneFontSize,
              fields: Object.fromEntries(Object.keys(next.fields).map(key => [key, {
                ...next.fields[key],
                fontSize: defaults.fields[key].fontSize
              }]))
            };
          })}>Use template font sizes</button>
          <button style={{
            ...S.btnGhost,
            minHeight: 36,
            marginLeft: "auto"
          }} onClick={restoreDefaults} disabled={cascadeBusy || saving || logoLoading}>Restore defaults</button>
        </div>
        {message && <div style={message.type === "err" ? S.err : S.ok}>{message.text}</div>}
        {localMsg && <div style={localMsg.type === "err" ? S.err : S.ok}>{localMsg.text}</div>}
        {cascadeResult && <div style={cascadeResult.failed ? S.err : S.ok}>
          Sent {cascadeResult.sent}; failed {cascadeResult.failed}.
          {(cascadeResult.results || []).filter(r => !r.sent).map(r => <div key={r.dbName}>{r.pharmacyName}: {r.reason}</div>)}
        </div>}
      </div>
    </div>

    {cascadeModalOpen && <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,42,0.55)",
      zIndex: 50,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20
    }} onClick={() => !cascadeBusy && setCascadeModalOpen(false)}>
        <div style={{
        background: "#fff",
        borderRadius: 10,
        padding: 24,
        maxWidth: 480,
        width: "100%",
        maxHeight: "82vh",
        display: "flex",
        flexDirection: "column"
      }} onClick={e => e.stopPropagation()}>
          <div style={{
          fontWeight: 700,
          fontSize: 15,
          marginBottom: 4
        }}>Cascade to branches</div>
          <div style={{
          fontSize: 12.5,
          color: "#5D5D5D",
          marginBottom: 14
        }}>
            Select the branches to send this poster to — each gets its own QR code. Untick any branch you don't
            want to send to right now.
          </div>

          {cascadeLoadErr && <div style={S.err}>{cascadeLoadErr}</div>}
          {!cascadeBranches && !cascadeLoadErr && <div style={{
          color: "#5D5D5D",
          fontSize: 13
        }}>Loading branches…</div>}

          {cascadeBranches && <>
              <label style={{
            fontSize: 13,
            fontWeight: 700,
            color: "#434343",
            display: "flex",
            alignItems: "center",
            gap: 8,
            borderBottom: "1px solid #F0F0F0",
            paddingBottom: 8,
            marginBottom: 6
          }}>
                <input type="checkbox" checked={allCascadeChecked} onChange={e => toggleCascadeAll(e.target.checked)} />
                Select all / Unselect all
              </label>
              <div style={{
            overflowY: "auto",
            flex: 1,
            minHeight: 60,
            maxHeight: 300
          }}>
                {cascadeBranches.map(b => <label key={b.dbName} style={{
              fontSize: 13.5,
              color: "#434343",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 0"
            }}>
                    <input type="checkbox" checked={b.checked} onChange={() => toggleCascadeBranch(b.dbName)} />
                    {b.pharmacyName}
                  </label>)}
                {cascadeBranches.length === 0 && <div style={{
              color: "#5D5D5D",
              fontSize: 13
            }}>No branches found.</div>}
              </div>
            </>}

          {localMsg && <div style={localMsg.type === "err" ? S.err : S.ok}>{localMsg.text}</div>}

          <div style={{
          display: "flex",
          gap: 8,
          marginTop: 16
        }}>
            <button style={S.btnDanger} onClick={sendCascadeToSelected} disabled={cascadeBusy || !cascadeBranches || !cascadeBranches.some(b => b.checked)}>
              {cascadeBusy ? "Sending…" : "Send to selected"}
            </button>
            <button style={S.btnGhost} onClick={() => setCascadeModalOpen(false)} disabled={cascadeBusy}>Cancel</button>
          </div>
        </div>
      </div>}
    </>;
}

// =============================================================================
// WEBPAGE DESIGN TAB (+ shared preview renderer)
// =============================================================================
const WEB_CANVAS = {
  w: 340,
  h: 140
}; // just the header strip where the name can move

function StarRating({
  value,
  onChange,
  readOnly
}) {
  return <div style={{
    display: "flex",
    gap: 4
  }}>
      {[1, 2, 3, 4, 5].map(n => <span key={n} onClick={() => !readOnly && onChange(n)} style={{
      fontSize: 26,
      cursor: readOnly ? "default" : "pointer",
      color: n <= value ? "#FA8C16" : "#D9D9D9"
    }}>
          ★
        </span>)}
    </div>;
}

// Rendered both by the in-editor "Preview" modal and (independently, in its
// own file) by the real public page — kept intentionally simple/duplicated
// rather than shared, so the public page has zero dependency on this module.
function FeedbackFormPreview({
  webpage,
  pharmacyName,
  contactDetails,
  hidePharmacyName,
  interactive,
  onSubmit,
  submitting,
  submitted,
  submitError
}) {
  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState("");
  const [isComplaint, setIsComplaint] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const align = webpage.pharmacyName.align || "center";
  return <FeedbackContactLayout webpage={webpage} details={contactDetails} pharmacyName={pharmacyName}>
    <div style={{
      maxWidth: 420,
      margin: "0 auto",
      fontFamily: "system-ui, sans-serif"
    }}>
      {!hidePharmacyName && !webpage.pharmacyName.positioned && <div style={{
        textAlign: align,
        fontSize: webpage.pharmacyName.fontSize,
        color: webpage.pharmacyName.fg,
        fontWeight: 700,
        marginBottom: 14
      }}>
        {pharmacyName || "Pharmacy name"}
      </div>}
      <div style={{
        fontSize: 15,
        color: "#434343",
        marginBottom: 18,
        textAlign: "center"
      }}>{webpage.introText}</div>

      {webpage.showRating && <div style={{
        marginBottom: 18,
        textAlign: "center"
      }}>
          <div style={{
          fontSize: 12.5,
          fontWeight: 700,
          color: "#5D5D5D",
          marginBottom: 4
        }}>HOW WAS YOUR VISIT?</div>
          {webpage.ratingHelp && <div style={{
          fontSize: 12,
          color: "#8C8C8C",
          marginBottom: 8
        }}>{webpage.ratingHelp}</div>}
          <div style={{
          display: "flex",
          justifyContent: "center"
        }}>
            <StarRating value={rating} onChange={setRating} readOnly={!interactive} />
          </div>
        </div>}

      {webpage.showComments && <div style={{
        marginBottom: 14
      }}>
          <label style={{
          fontSize: 12.5,
          fontWeight: 700,
          color: "#5D5D5D",
          display: "block",
          marginBottom: 2
        }}>
            FEEDBACK OR COMMENTS
          </label>
          {webpage.commentsHelp && <div style={{
          fontSize: 12,
          color: "#8C8C8C",
          marginBottom: 6
        }}>{webpage.commentsHelp}</div>}
          <textarea style={{
          ...S.textarea,
          minHeight: 90
        }} value={comments} readOnly={!interactive} onChange={e => setComments(e.target.value)} placeholder="Tell us what you think…" />
        </div>}

      {webpage.showComplaint && <div style={{
        marginBottom: 14
      }}>
          <label style={{
          fontSize: 13,
          color: "#434343",
          display: "flex",
          alignItems: "center",
          gap: 8
        }}>
            <input type="checkbox" checked={isComplaint} disabled={!interactive} onChange={e => setIsComplaint(e.target.checked)} />
            This is a complaint
          </label>
          {webpage.complaintHelp && <div style={{
          fontSize: 12,
          color: "#8C8C8C",
          marginTop: 4,
          marginLeft: 24
        }}>{webpage.complaintHelp}</div>}
        </div>}

      {<>
          <label style={S.label}>
            Your name {isComplaint ? <span style={{
            color: "#CF1322"
          }}>(required for a complaint)</span> : "(optional)"}
          </label>
          <input style={S.input} readOnly={!interactive} value={customerName} onChange={e => setCustomerName(e.target.value)} required={isComplaint} />
          <label style={S.label}>
            Your email {isComplaint ? <span style={{
            color: "#CF1322"
          }}>(required for a complaint, so we can respond)</span> : "(optional — only needed if you'd like a reply)"}
          </label>
          <input style={S.input} readOnly={!interactive} value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} required={isComplaint} />
        </>}

      {interactive ? submitted ? <div style={{
        ...S.ok,
        marginTop: 16,
        textAlign: "center"
      }}>Thank you — your feedback has been sent.</div> : <>
            <button style={{
          ...S.btn,
          width: "100%",
          marginTop: 16,
          padding: "11px 0"
        }} disabled={submitting} onClick={() => onSubmit({
          rating,
          comments,
          isComplaint,
          customerName,
          customerEmail
        })}>
              {submitting ? "Sending…" : "Send"}
            </button>
            {submitError && <div style={S.err}>{submitError}</div>}
          </> : <button style={{
        ...S.btn,
        width: "100%",
        marginTop: 16,
        padding: "11px 0",
        opacity: 0.6,
        cursor: "default"
      }}>Send</button>}
    </div>
    </FeedbackContactLayout>;
}
function WebpageDesignTab({
  webpage,
  setWebpage,
  saving,
  onSave,
  message,
  trustpilotConfigured
}) {
  const [showPreview, setShowPreview] = useState(false);
  const [localMsg, setLocalMsg] = useState(null);
  const [selectedContact, setSelectedContact] = useState("telephone");
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [contactSaving, setContactSaving] = useState(false);
  const [contactDirty, setContactDirty] = useState(false);
  const [contactMessage, setContactMessage] = useState(null);
  useEffect(() => {
    let active = true;
    apiPost("/api/feedback/branches", {}).then(r => {
      if (!active) return;
      setBranches(r.branches || []);
      setBranchId((r.branches || [])[0]?.dbName || "");
    }).catch(e => {
      if (active) setContactMessage({
        type: "err",
        text: e.message
      });
    });
    return () => {
      active = false;
    };
  }, []);
  const branch = branches.find(b => b.dbName === branchId);
  const details = {
    telephone: "",
    email: "",
    pharmacistName: "",
    ...branch?.feedbackContact
  };
  const height = contactHeight(webpage);
  const nameBlock = {
    ...webpage.pharmacyName,
    x: webpage.pharmacyName.positioned ? webpage.pharmacyName.x : webpage.pharmacyName.align === "left" ? 0 : webpage.pharmacyName.align === "right" ? 40 : 20,
    y: webpage.pharmacyName.positioned ? webpage.pharmacyName.y : 0,
    w: webpage.pharmacyName.w || 60,
    h: webpage.pharmacyName.h || 4
  };
  const selectedBlock = contactBlock(webpage, selectedContact === "pharmacyName" ? "telephone" : selectedContact);
  const updateContactBlock = (key, patch) => setWebpage(w => ({
    ...w,
    contactBlocks: {
      ...w.contactBlocks,
      [key]: contactBlock({
        contactBlocks: {
          [key]: {
            ...contactBlock(w, key),
            ...patch
          }
        }
      }, key)
    }
  }));
  const editContact = (key, value) => {
    setContactDirty(true);
    setContactMessage(null);
    setBranches(rows => rows.map(b => b.dbName === branchId ? {
      ...b,
      feedbackContact: {
        ...b.feedbackContact,
        [key]: value
      }
    } : b));
  };
  const saveContact = async () => {
    setContactSaving(true);
    setContactMessage(null);
    try {
      const r = await apiPost("/api/feedback/branches/contact", {
        dbName: branchId,
        contact: details
      });
      setBranches(rows => rows.map(b => b.dbName === branchId ? {
        ...b,
        feedbackContact: r.contact
      } : b));
      setContactDirty(false);
      setContactMessage({
        type: "ok",
        text: "Public branch details saved."
      });
    } catch (e) {
      setContactMessage({
        type: "err",
        text: e.message
      });
    }
    setContactSaving(false);
  };
  const restoreDefaults = async () => {
    if (!(await appConfirm("Reset the webpage's text, colours, fonts and shown sections back to the original defaults? This can't be undone unless you saved a version you want to keep."))) return;
    setWebpage(cloneDefault(DEFAULT_WEBPAGE));
    setLocalMsg({
      type: "ok",
      text: "Restored to defaults — click Save design to keep this."
    });
  };
  return <div style={S.panelRow}>
      <div style={{
      ...S.card,
      width: WEB_CANVAS.w + 28
    }}>
        <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginBottom: 8
      }}>
          Drag the pharmacy name and contact blocks anywhere on the page. Click a block to select it; use its purple corner to resize.
        </div>
        <div style={{
        position: "relative",
        width: WEB_CANVAS.w,
        height,
        marginTop: 16,
        border: "1px solid #D9D9D9",
        boxSizing: "content-box"
      }}>
          <div style={{
          paddingTop: 50
        }}>
          <FeedbackFormPreview hidePharmacyName webpage={{
            ...webpage,
            pharmacyName: {
              ...webpage.pharmacyName,
              positioned: false
            },
            contactBlocks: Object.fromEntries(CONTACT_FIELDS.map(([key]) => [key, {
              ...contactBlock(webpage, key),
              enabled: false
            }]))
          }} pharmacyName={branch?.pharmacyName || "Sample Pharmacy"} interactive={false} />
          </div>
          <DesignBlock block={nameBlock} canvasSize={{
          w: WEB_CANVAS.w,
          h: height
        }} minH={2} onSelect={() => setSelectedContact("pharmacyName")} selected={selectedContact === "pharmacyName"} onAlign={align => setWebpage(w => ({
          ...w,
          pharmacyName: {
            ...w.pharmacyName,
            align
          }
        }))} onChange={b => setWebpage(w => ({
          ...w,
          pharmacyName: {
            ...w.pharmacyName,
            ...b,
            positioned: true
          }
        }))}>
            <div style={{
            fontSize: webpage.pharmacyName.fontSize,
            color: webpage.pharmacyName.fg,
            fontWeight: 700,
            textAlign: webpage.pharmacyName.align,
            userSelect: "none",
            overflowWrap: "anywhere"
          }}>{branch?.pharmacyName || "Sample Pharmacy"}</div>
          </DesignBlock>
          {CONTACT_FIELDS.filter(([key]) => contactBlock(webpage, key).enabled).map(([key, label]) => <DesignBlock key={key} block={contactBlock(webpage, key)} canvasSize={{
          w: WEB_CANVAS.w,
          h: height
        }} minH={2} onChange={b => updateContactBlock(key, b)} onSelect={() => setSelectedContact(key)} selected={selectedContact === key} onAlign={align => updateContactBlock(key, {
          align
        })}>
            <div style={{
            fontSize: contactBlock(webpage, key).fontSize,
            color: contactBlock(webpage, key).fg,
            textAlign: contactBlock(webpage, key).align,
            overflowWrap: "anywhere",
            width: "100%",
            height: "100%",
            userSelect: "none",
            background: "rgba(255,255,255,.85)"
          }}>
              {details[key] || label}
            </div>
          </DesignBlock>)}
        </div>
        <div style={{
        marginTop: 12,
        fontSize: 12,
        color: "#8C8C8C"
      }}>
          Enable a contact block on the right, then drag it to the desired position. Keep it clear of form fields; use Preview to check the layout.
        </div>
      </div>

      <div style={{
      ...S.card,
      minWidth: 260,
      flex: 1
    }}>
        <div style={{
        fontWeight: 700,
        fontSize: 14
      }}>Public branch details</div>
        <label style={S.label}>Branch to edit / preview</label>
        <select style={S.input} value={branchId} disabled={contactSaving} onChange={async e => {
        if (contactDirty && !(await appConfirm("Discard unsaved branch contact details?"))) return;
        setBranchId(e.target.value);
        setContactDirty(false);
        setContactMessage(null);
      }}>
          {branches.map(b => <option key={b.dbName} value={b.dbName}>{b.pharmacyName}</option>)}
        </select>
        {CONTACT_FIELDS.map(([key, label]) => <div key={key}>
          <label style={S.label}>{label}</label>
          <input style={S.input} type={key === "email" ? "email" : key === "telephone" ? "tel" : "text"} value={details[key] || ""} disabled={!branch || contactSaving} onChange={e => editContact(key, e.target.value)} />
        </div>)}
        <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginTop: 8
      }}>These details are public on this branch's feedback page when their blocks are enabled.</div>
        <button style={{
        ...S.btnGhost,
        marginTop: 8
      }} onClick={saveContact} disabled={!branch || contactSaving || !contactDirty}>
          {contactSaving ? "Saving…" : "Save branch details"}
        </button>
        {contactMessage && <div style={contactMessage.type === "err" ? S.err : S.ok}>{contactMessage.text}</div>}
        <div style={{
        borderTop: "1px solid #F0F0F0",
        margin: "14px 0"
      }} />
        <div style={{
        fontWeight: 700,
        fontSize: 14
      }}>Contact block layout (all branches)</div>
        {CONTACT_FIELDS.map(([key, label]) => <label key={key} style={{
        display: "flex",
        gap: 8,
        marginTop: 8,
        fontSize: 13
      }}>
          <input type="checkbox" checked={contactBlock(webpage, key).enabled} onChange={e => {
          updateContactBlock(key, {
            enabled: e.target.checked
          });
          setSelectedContact(key);
        }} />{label}
        </label>)}
        <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginTop: 12
      }}>Click a contact block on the page to edit its font, colour and alignment below.</div>
        {selectedContact !== "pharmacyName" && <>
        <label style={S.label}>Contact font size</label>
        <input type="range" min="10" max="30" value={selectedBlock.fontSize} style={{
          width: "100%"
        }} onChange={e => updateContactBlock(selectedContact, {
          fontSize: Number(e.target.value)
        })} />
        <ColorField label="Contact text colour" value={selectedBlock.fg} onChange={fg => updateContactBlock(selectedContact, {
          fg
        })} />
        <label style={S.label}>Contact text alignment</label>
        <select style={S.input} value={selectedBlock.align} onChange={e => updateContactBlock(selectedContact, {
          align: e.target.value
        })}>
          {["left", "center", "right", "justify"].map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        </>}
        <label style={S.label}>Page canvas height (pixels)</label>
        <input style={S.input} type="number" min="800" max="2400" value={height} onChange={e => {
        if (e.target.value !== "") setWebpage(w => ({
          ...w,
          contactCanvasHeight: clamp(Number(e.target.value), 800, 2400)
        }));
      }} />
        <div style={{
        borderTop: "1px solid #F0F0F0",
        margin: "14px 0"
      }} />
        <label style={S.label}>Alignment</label>
        <div style={{
        display: "flex",
        gap: 6
      }}>
          {["left", "center", "right", "justify"].map(a => <button key={a} style={{
          ...S.btnGhost,
          flex: 1,
          background: webpage.pharmacyName.align === a ? "#F9F0FF" : "#fff",
          borderColor: webpage.pharmacyName.align === a ? "#722ED1" : "#D9D9D9"
        }} onClick={() => setWebpage(w => ({
          ...w,
          pharmacyName: {
            ...w.pharmacyName,
            align: a
          }
        }))}>
              {a[0].toUpperCase() + a.slice(1)}
            </button>)}
        </div>

        <label style={S.label}>Font size</label>
        <input type="range" min="14" max="40" value={webpage.pharmacyName.fontSize} onChange={e => setWebpage(w => ({
        ...w,
        pharmacyName: {
          ...w.pharmacyName,
          fontSize: Number(e.target.value)
        }
      }))} style={{
        width: "100%"
      }} />

        <ColorField label="Name colour" value={webpage.pharmacyName.fg} onChange={v => setWebpage(w => ({
        ...w,
        pharmacyName: {
          ...w.pharmacyName,
          fg: v
        }
      }))} />

        <label style={S.label}>Intro text</label>
        <input style={S.input} value={webpage.introText} onChange={e => setWebpage(w => ({
        ...w,
        introText: e.target.value
      }))} />

        <div style={{
        borderTop: "1px solid #F0F0F0",
        margin: "14px 0 10px"
      }} />
        <div style={{
        fontWeight: 700,
        fontSize: 13,
        marginBottom: 6
      }}>Show on the form</div>
        {[["showRating", "Satisfaction rating", "ratingHelp"], ["showComments", "Feedback / comments", "commentsHelp"], ["showComplaint", "Complaint option", "complaintHelp"]].map(([k, l, helpKey]) => <div key={k} style={{
        marginBottom: 10
      }}>
            <label style={{
          fontSize: 13,
          color: "#434343",
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4
        }}>
              <input type="checkbox" checked={webpage[k]} onChange={e => setWebpage(w => ({
            ...w,
            [k]: e.target.checked
          }))} />
              {l}
            </label>
            {webpage[k] && <input style={{
          ...S.input,
          marginLeft: 24,
          width: "calc(100% - 24px)"
        }} value={webpage[helpKey] || ""} placeholder="Explanation shown to the customer under this section…" onChange={e => setWebpage(w => ({
          ...w,
          [helpKey]: e.target.value
        }))} />}
          </div>)}

        <div style={{
        borderTop: "1px solid #F0F0F0",
        margin: "14px 0 10px"
      }} />
        <div style={{
        opacity: 0.5
      }}>
          <label style={{
          fontSize: 13,
          color: "#434343",
          display: "flex",
          alignItems: "center",
          gap: 8
        }}>
            <input type="checkbox" checked={!!webpage.showTrustpilotInvite} disabled onChange={e => setWebpage(w => ({
            ...w,
            showTrustpilotInvite: e.target.checked
          }))} />
            Invite to give Trustpilot review
          </label>
          {!trustpilotConfigured && <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          margin: "5px 0 0 24px"
        }}>
              Trustpilot invitations are currently unavailable.
            </div>}
          {webpage.showTrustpilotInvite && trustpilotConfigured && <textarea disabled style={{
          ...S.textarea,
          margin: "8px 0 0 24px",
          width: "calc(100% - 24px)"
        }} value={webpage.trustpilotInviteText || ""} placeholder="What would you like to say before the Trustpilot button?" onChange={e => setWebpage(w => ({
          ...w,
          trustpilotInviteText: e.target.value
        }))} />}
        </div>

        <div style={{
        display: "flex",
        gap: 8,
        marginTop: 14,
        flexWrap: "wrap"
      }}>
          <button style={S.btn} onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save design"}</button>
          <button style={S.btnGhost} onClick={() => setShowPreview(true)}>Preview</button>
        </div>
        <div style={{
        marginTop: 8
      }}>
          <button style={S.btnGhost} onClick={restoreDefaults}>Restore defaults</button>
        </div>
        {message && <div style={message.type === "err" ? S.err : S.ok}>{message.text}</div>}
        {localMsg && <div style={localMsg.type === "err" ? S.err : S.ok}>{localMsg.text}</div>}
      </div>

      {showPreview && <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,42,0.55)",
      zIndex: 50,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20
    }} onClick={() => setShowPreview(false)}>
          <div style={{
        background: "#fff",
        borderRadius: 10,
        padding: 28,
        maxWidth: 480,
        width: "100%",
        maxHeight: "88vh",
        overflow: "auto"
      }} onClick={e => e.stopPropagation()}>
            <FeedbackFormPreview webpage={webpage} pharmacyName={branch?.pharmacyName || "Sample Pharmacy"} contactDetails={details} interactive={false} />
            <button style={{
          ...S.btnGhost,
          width: "100%",
          marginTop: 16
        }} onClick={() => setShowPreview(false)}>Close preview</button>
          </div>
        </div>}
    </div>;
}

// =============================================================================
// ACKNOWLEDGEMENT TEXT TAB — item (b): configurable covering text sent
// straight back to the patient the moment a complaint is submitted (item
// a). A peer of Poster design / Webpage design, but self-contained (its
// own load/save) since it doesn't share ConfigSection's poster+webpage
// state.
// =============================================================================
function AcknowledgementTab() {
  const [contactDetails, setContactDetails] = useState({
    telephone: {
      enabled: false,
      prefix: "Telephone: "
    },
    email: {
      enabled: false,
      prefix: "Email: "
    },
    pharmacistName: {
      enabled: false,
      prefix: "Pharmacist: "
    }
  });
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const cfg = await apiPost("/api/feedback/config", {});
        setContactDetails(c => Object.fromEntries(Object.keys(c).map(key => [key, {
          ...c[key],
          ...cfg.acknowledgement?.contactDetails?.[key]
        }])));
        setText(cfg.acknowledgement && cfg.acknowledgement.text || DEFAULT_ACKNOWLEDGEMENT_TEXT);
      } catch (e) {
        setErr(e.message);
      }
      setLoading(false);
    })();
  }, []);
  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPost("/api/feedback/config/save", {
        acknowledgement: {
          text,
          contactDetails
        }
      });
      setMessage({
        type: "ok",
        text: "Saved."
      });
    } catch (e) {
      setMessage({
        type: "err",
        text: e.message
      });
    }
    setSaving(false);
  };
  const restoreDefaults = async () => {
    if (!(await appConfirm("Reset the acknowledgement text back to the original default? This can't be undone unless you saved a version you want to keep."))) return;
    setText(DEFAULT_ACKNOWLEDGEMENT_TEXT);
    setMessage({
      type: "ok",
      text: "Restored to default — click Save to keep this."
    });
  };
  if (loading) return <div style={{
    color: "#5D5D5D",
    fontSize: 13
  }}>Loading…</div>;
  return <div style={{
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "stretch"
  }}>
    <div style={{
      ...S.card,
      flex: "1 1 420px",
      maxWidth: 560
    }}>
      <div style={{
        fontWeight: 700,
        fontSize: 14,
        marginBottom: 4
      }}>Acknowledgement text</div>
      <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginBottom: 10
      }}>
        Sent straight back to the customer the moment they submit a complaint, before the branch manager has
        even looked at it. Use <code>{"{{pharmacyName}}"}</code> anywhere you'd like that branch's name inserted.
      </div>
      {err && <div style={S.err}>{err}</div>}
      <textarea style={{
        ...S.textarea,
        minHeight: 160
      }} value={text} onChange={e => setText(e.target.value)} />
      <div style={{
        display: "flex",
        gap: 8,
        marginTop: 10
      }}>
        <button style={S.btn} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        <button style={S.btnGhost} onClick={restoreDefaults}>Restore default</button>
      </div>
      {message && <div style={message.type === "err" ? S.err : S.ok}>{message.text}</div>}
    </div>
    <div style={{
      ...S.card,
      flex: "1 1 320px",
      maxWidth: 480
    }}>
      <div style={{
        fontWeight: 700,
        fontSize: 14
      }}>Include in the acknowledgement</div>
      <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginTop: 6
      }}>Uses the public details saved for that branch in Webpage design. Blank details are omitted.</div>
      {CONTACT_FIELDS.map(([key, label]) => <div key={key} style={{
        marginTop: 16
      }}>
        <label style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13
        }}>
          <input type="checkbox" checked={contactDetails[key].enabled} onChange={e => setContactDetails(c => ({
            ...c,
            [key]: {
              ...c[key],
              enabled: e.target.checked
            }
          }))} />{label}
        </label>
        <label style={S.label}>Text before the detail</label>
        <input style={S.input} value={contactDetails[key].prefix} onChange={e => setContactDetails(c => ({
          ...c,
          [key]: {
            ...c[key],
            prefix: e.target.value
          }
        }))} />
      </div>)}
      <button style={{
        ...S.btn,
        marginTop: 16
      }} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save acknowledgement"}</button>
    </div>
    </div>;
}

// =============================================================================
// BRANCHES TAB — grid of counts + Excel export
// =============================================================================
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function monthStartISO() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function yearStartISO() {
  const d = new Date();
  d.setMonth(0, 1);
  return d.toISOString().slice(0, 10);
}
function BranchesTab() {
  const [from, setFrom] = useState(monthStartISO());
  const [to, setTo] = useState(todayISO());
  const [rows, setRows] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const load = useCallback(async (f, t) => {
    setLoading(true);
    setErr(null);
    try {
      const r = await apiPost("/api/feedback/summary", {
        from: f || undefined,
        to: t || undefined
      });
      setRows(r.branches);
      setOrgName(r.organisationName);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }, []);
  useEffect(() => {
    load(from, to);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Same shape as feedback_common.js's buildFeedbackWorkbookBuffer() on the
  // server, so a report emailed automatically and one exported by hand
  // here are the identical spreadsheet.
  const exportExcel = () => {
    if (!rows) return;
    const wb = XLSX.utils.book_new();
    const header = [["Organisation", orgName], ["Date printed", todayISO()], ["Date range", `${from || "all time"} to ${to || todayISO()}`], [], ["Branch", "Total responses", "Average rating", "Rated responses", "Comments", "Complaints", "Complaints reviewed", "Awaiting review"]];
    const body = rows.map(r => [r.pharmacyName, r.total, r.avgRating ?? "", r.ratingCount, r.commentsCount, r.complaints, r.complaintsReviewed, r.pending]);
    const ws = XLSX.utils.aoa_to_sheet([...header, ...body]);
    XLSX.utils.book_append_sheet(wb, ws, "Patient feedback");
    XLSX.writeFile(wb, `patient_feedback_${from || "all"}_to_${to || todayISO()}.xlsx`);
  };
  return <div>
      <div style={{
      display: "flex",
      gap: 8,
      alignItems: "flex-end",
      flexWrap: "wrap",
      marginBottom: 14
    }}>
        <div>
          <label style={S.label}>From</label>
          <input type="date" style={S.input} value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div>
          <label style={S.label}>To</label>
          <input type="date" style={S.input} value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <button style={S.btnGhost} onClick={() => load(from, to)}>Apply</button>
        <button style={S.btnGhost} onClick={() => {
        const f = monthStartISO(),
          t = todayISO();
        setFrom(f);
        setTo(t);
        load(f, t);
      }}>This month</button>
        <button style={S.btnGhost} onClick={() => {
        const f = yearStartISO(),
          t = todayISO();
        setFrom(f);
        setTo(t);
        load(f, t);
      }}>This year</button>
        <button style={S.btnGhost} onClick={() => {
        setFrom("");
        setTo("");
        load("", "");
      }}>All time</button>
        <div style={{
        flex: 1
      }} />
        <button style={S.btn} onClick={exportExcel} disabled={!rows || loading}>Export to Excel</button>
      </div>

      {err && <div style={S.err}>{err}</div>}
      {loading && <div style={{
      color: "#5D5D5D",
      fontSize: 13
    }}>Loading…</div>}

      {rows && <div style={{
      ...S.card,
      overflowX: "auto"
    }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Branch</th>
                <th style={S.th}>Total</th>
                <th style={S.th}>Avg rating</th>
                <th style={S.th}>Comments</th>
                <th style={S.th}>Complaints</th>
                <th style={S.th}>Complaints reviewed</th>
                <th style={S.th}>Awaiting review</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => <tr key={r.dbName}>
                  <td style={S.td}>{r.pharmacyName}</td>
                  <td style={S.td}>{r.total}</td>
                  <td style={S.td}>{r.avgRating != null ? `${r.avgRating} ★ (${r.ratingCount})` : "—"}</td>
                  <td style={S.td}>{r.commentsCount}</td>
                  <td style={{
              ...S.td,
              color: r.complaints ? "#CF1322" : undefined,
              fontWeight: r.complaints ? 700 : 400
            }}>{r.complaints}</td>
                  <td style={S.td}>{r.complaintsReviewed}</td>
                  <td style={{
              ...S.td,
              color: r.pending ? "#b45309" : undefined,
              fontWeight: r.pending ? 700 : 400
            }}>{r.pending}</td>
                </tr>)}
              {rows.length === 0 && <tr><td style={S.td} colSpan={7}>No branches found.</td></tr>}
            </tbody>
          </table>
        </div>}
    </div>;
}

// =============================================================================
// INBOX TAB
// =============================================================================
function StarsReadout({
  n
}) {
  if (!n) return <span style={{
    color: "#8C8C8C"
  }}>—</span>;
  return <span style={{
    color: "#FA8C16"
  }}>{"★".repeat(n)}{"☆".repeat(5 - n)}</span>;
}

// 0 -> "first email", 1 -> "second email", 2 -> "third email"... matching
// reminderCount (the number of reminder nudges sent so far, on top of the
// original manager-copy email).
const EMAIL_ORDINAL_WORDS = ["first", "second", "third", "fourth", "fifth", "sixth", "seventh", "eighth", "ninth", "tenth"];
function emailOrdinalLabel(reminderCount) {
  const n = Number(reminderCount) || 0;
  return EMAIL_ORDINAL_WORDS[n] || `${n + 1}th`;
}
function InboxRow({
  entry,
  onSaved
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const closed = !!(entry.outcome && entry.outcome.closedAt);
  const awaitingReview = !entry.reviewed && !closed;
  const setReviewed = async reviewed => {
    setBusy(true);
    setMsg(null);
    try {
      await apiPost("/api/feedback/inbox/review", {
        dbName: entry.dbName,
        id: entry._id,
        reviewed
      });
      onSaved();
    } catch (e) {
      setMsg(e.message);
    }
    setBusy(false);
  };
  const lastEmailAt = entry.lastReminderAt || entry.managerEmailedAt;
  return <div style={{
    ...S.card,
    marginBottom: 10
  }}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 8
    }}>
        <div>
          <div style={{
          fontWeight: 700,
          fontSize: 14
        }}>
            {entry.pharmacyName}
            {entry.isComplaint && <span style={{
            marginLeft: 8,
            fontSize: 11,
            fontWeight: 700,
            color: "#A8071A",
            background: "#FFF1F0",
            border: "1px solid #FFCCC7",
            borderRadius: 4,
            padding: "2px 6px"
          }}>
                COMPLAINT
              </span>}
            {awaitingReview && <span style={{
            marginLeft: 8,
            fontSize: 11,
            fontWeight: 700,
            color: "#AD4E00",
            background: "#FFF7E6",
            border: "1px solid #FFE58F",
            borderRadius: 4,
            padding: "2px 6px"
          }}>
                AWAITING REVIEW
              </span>}
            {entry.reviewed && !closed && <span style={{
            marginLeft: 8,
            fontSize: 11,
            fontWeight: 700,
            color: "#389E0D",
            background: "#F6FFED",
            border: "1px solid #B7EB8F",
            borderRadius: 4,
            padding: "2px 6px"
          }}>
                REVIEWED
              </span>}
            {closed && <span style={{
            marginLeft: 8,
            fontSize: 11,
            fontWeight: 700,
            color: "#434343",
            background: "#F0F0F0",
            border: "1px solid #D9D9D9",
            borderRadius: 4,
            padding: "2px 6px"
          }}>
                CLOSED
              </span>}
          </div>
        </div>
      </div>

      {/* Contents, laid out in the requested labeled sections. */}
      <div style={{
      marginTop: 10,
      fontSize: 13.5,
      color: "#434343"
    }}>
        <div>
          <b>From:</b> {entry.customerName || "—"}{entry.customerEmail ? ` ${entry.customerEmail}` : ""}
        </div>
        <div style={{
        marginTop: 4,
        display: "flex",
        alignItems: "center",
        gap: 6
      }}>
          <b>Star rating:</b> <StarsReadout n={entry.rating} />
        </div>
        {entry.comments && <div style={{
        marginTop: 6
      }}>
            <b>Comment</b>
            <div style={{
          marginTop: 2,
          whiteSpace: "pre-wrap"
        }}>{entry.comments}</div>
          </div>}

        <div style={{
        marginTop: 10
      }}>
          <b>Time of complaint:</b> {entry.submittedAt ? new Date(entry.submittedAt).toLocaleString() : "—"}
        </div>

        {entry.managerEmailedAt && <div style={{
        marginTop: 4
      }}>
            <b>Time of last email to manager:</b> {lastEmailAt ? new Date(lastEmailAt).toLocaleString() : "—"}
            {" "}({emailOrdinalLabel(entry.reminderCount)} email)
          </div>}

        {entry.managerEmailedAt && <div style={{
        marginTop: 4
      }}>
            <b>Manager's name:</b> {entry.managerEmailedToName || "—"}{entry.managerEmailedTo ? ` ${entry.managerEmailedTo}` : ""}
          </div>}
      </div>

      {/* Complaint email trail — acknowledgement to the patient and any
          superintendent referral, kept as a compact log-style summary
          alongside the sections above (which now cover the manager email). */}
      {entry.isComplaint && (entry.acknowledgementSentAt || entry.superintendentEmailedAt) && <div style={{
      fontSize: 11.5,
      color: "#5D5D5D",
      marginTop: 8,
      background: "#FAFAFA",
      border: "1px solid #F5F5F5",
      borderRadius: 6,
      padding: "6px 9px"
    }}>
          {entry.acknowledgementSentAt && <div>Acknowledged to patient · {new Date(entry.acknowledgementSentAt).toLocaleString()}</div>}
          {entry.superintendentEmailedAt && <div>Referred to superintendent · {new Date(entry.superintendentEmailedAt).toLocaleString()}</div>}
        </div>}

      {/* Recorded outcome (item f) — shown as soon as the branch manager has
          saved anything from the emailed outcome-recording link, on both
          the Awaiting review and Reviewed views. */}
      {entry.outcome && <div style={{
      marginTop: 8,
      background: "#faf5ff",
      border: "1px solid #F9F0FF",
      borderRadius: 6,
      padding: "8px 10px"
    }}>
          <div style={{
        fontSize: 11.5,
        fontWeight: 700,
        color: "#6b21a8",
        marginBottom: 4
      }}>
            Complaint outcome {entry.outcome.savedAt && `— saved ${new Date(entry.outcome.savedAt).toLocaleString()}`}
          </div>
          {entry.outcome.responseToPatient && <div style={{
        fontSize: 13,
        color: "#434343",
        marginTop: 4
      }}>
              <b>Response to patient:</b> {entry.outcome.responseToPatient}
            </div>}
          {entry.outcome.responseDateTime && <div style={{
        fontSize: 13,
        color: "#434343",
        marginTop: 4
      }}>
              <b>Response date/time:</b> {entry.outcome.responseDateTime}
            </div>}
          {entry.outcome.investigationOutcome && <div style={{
        fontSize: 13,
        color: "#434343",
        marginTop: 4
      }}>
              <b>Investigation outcome:</b> {entry.outcome.investigationOutcome}
            </div>}
          {entry.outcome.actionTaken && <div style={{
        fontSize: 13,
        color: "#434343",
        marginTop: 4
      }}>
              <b>Action taken:</b> {entry.outcome.actionTaken}
            </div>}
          <div style={{
        fontSize: 12.5,
        color: "#434343",
        marginTop: 4
      }}>
            <b>Superintendent to be informed:</b> {entry.outcome.superintendentInformed ? "Yes" : "No"}
          </div>
        </div>}

      <div style={{
      display: "flex",
      gap: 8,
      marginTop: 10,
      flexWrap: "wrap"
    }}>
        {!entry.reviewed && <button style={S.btn} onClick={() => setReviewed(true)} disabled={busy}>Mark reviewed</button>}
        {entry.reviewed && <button style={S.btnGhost} onClick={() => setReviewed(false)} disabled={busy}>Reopen</button>}
      </div>

      {msg && <div style={S.err}>{msg}</div>}
    </div>;
}
function InboxTab() {
  const [status, setStatus] = useState("pending"); // 'pending' | 'reviewed' | 'all'
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const load = useCallback(async s => {
    setLoading(true);
    setErr(null);
    try {
      const r = await apiPost("/api/feedback/inbox/list", {
        status: s
      });
      setEntries(r.entries);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }, []);
  useEffect(() => {
    load(status);
  }, [status, load]);
  return <div>
      <div style={{
      display: "flex",
      gap: 6,
      marginBottom: 14
    }}>
        {[["pending", "Awaiting review"], ["reviewed", "Reviewed"], ["all", "All"]].map(([k, l]) => <button key={k} style={{
        ...S.btnGhost,
        background: status === k ? "#F9F0FF" : "#fff",
        borderColor: status === k ? "#722ED1" : "#D9D9D9"
      }} onClick={() => setStatus(k)}>
            {l}
          </button>)}
      </div>
      {err && <div style={S.err}>{err}</div>}
      {loading && <div style={{
      color: "#5D5D5D",
      fontSize: 13
    }}>Loading…</div>}
      {entries && entries.length === 0 && <div style={{
      color: "#5D5D5D",
      fontSize: 13
    }}>Nothing here.</div>}
      {entries && entries.map(e => <InboxRow key={`${e.dbName}_${e._id}`} entry={e} onSaved={() => load(status)} />)}
    </div>;
}

// =============================================================================
// LOG TAB — every submission, reply, report send, alert send and poster
// cascade send, newest first. Date range filter, same widget as Branches.
// Complements (doesn't replace) the per-entry replySentAt shown in Inbox.
// =============================================================================
const LOG_TYPE_STYLE = {
  "Submission": {
    bg: "#eef2ff",
    fg: "#1D39C4"
  },
  "Submission (complaint)": {
    bg: "#FFF1F0",
    fg: "#A8071A"
  },
  "Reply email": {
    bg: "#F6FFED",
    fg: "#389E0D"
  },
  "Report emailed": {
    bg: "#E6F7FF",
    fg: "#0958D9"
  },
  "Alert email": {
    bg: "#FFF7E6",
    fg: "#AD4E00"
  },
  "Poster cascade email": {
    bg: "#f3e8ff",
    fg: "#6b21a8"
  },
  "Acknowledgement email": {
    bg: "#ecfdf5",
    fg: "#065f46"
  },
  "Complaint email to manager": {
    bg: "#FFF1F0",
    fg: "#A8071A"
  },
  "Reminder email": {
    bg: "#FFF7E6",
    fg: "#AD4E00"
  },
  "Complaint copy email": {
    bg: "#FFF1F0",
    fg: "#A8071A"
  },
  "Reminder copy email": {
    bg: "#FFF7E6",
    fg: "#AD4E00"
  },
  "Superintendent notification copy email": {
    bg: "#F0F5FF",
    fg: "#1D39C4"
  },
  "Outcome saved": {
    bg: "#f3e8ff",
    fg: "#6b21a8"
  },
  "Outcome saved and closed": {
    bg: "#F0F0F0",
    fg: "#434343"
  },
  "Superintendent referral email": {
    bg: "#FFF1F0",
    fg: "#820014"
  }
};
function LogTab() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const load = useCallback(async (f, t) => {
    setLoading(true);
    setErr(null);
    try {
      const r = await apiPost("/api/feedback/log/list", {
        from: f || undefined,
        to: t || undefined
      });
      setEntries(r.entries);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }, []);
  useEffect(() => {
    load(from, to);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <div>
      <div style={{
      display: "flex",
      gap: 8,
      alignItems: "flex-end",
      flexWrap: "wrap",
      marginBottom: 14
    }}>
        <div>
          <label style={S.label}>From</label>
          <input type="date" style={S.input} value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div>
          <label style={S.label}>To</label>
          <input type="date" style={S.input} value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <button style={S.btnGhost} onClick={() => load(from, to)}>Apply</button>
        <button style={S.btnGhost} onClick={() => {
        setFrom("");
        setTo("");
        load("", "");
      }}>All time</button>
      </div>

      {err && <div style={S.err}>{err}</div>}
      {loading && <div style={{
      color: "#5D5D5D",
      fontSize: 13
    }}>Loading…</div>}

      {entries && <div style={{
      ...S.card,
      overflowX: "auto"
    }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Date</th>
                <th style={S.th}>Branch</th>
                <th style={S.th}>Event</th>
                <th style={S.th}>Recipient</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => {
            const tone = LOG_TYPE_STYLE[e.type] || {
              bg: "#F5F5F5",
              fg: "#434343"
            };
            return <tr key={e._id}>
                    <td style={S.td}>{new Date(e.date).toLocaleString()}</td>
                    <td style={S.td}>{e.branch || "—"}</td>
                    <td style={S.td}>
                      <span style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: tone.fg,
                  background: tone.bg,
                  border: `1px solid ${tone.fg}22`,
                  borderRadius: 4,
                  padding: "2px 6px"
                }}>
                        {e.type}
                      </span>
                      {e.detail && <span style={{
                  marginLeft: 6,
                  fontSize: 12,
                  color: "#5D5D5D"
                }}>{e.detail}</span>}
                    </td>
                    <td style={S.td}>{e.recipient || "—"}</td>
                  </tr>;
          })}
              {entries.length === 0 && <tr><td style={S.td} colSpan={4}>Nothing logged yet.</td></tr>}
            </tbody>
          </table>
        </div>}
    </div>;
}

// =============================================================================
// CONFIG TAB WRAPPER — loads/saves org config, hosts poster + webpage tabs
// =============================================================================
function ConfigSection({
  tab
}) {
  const [poster, setPoster] = useState(null);
  const [webpage, setWebpage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [err, setErr] = useState(null);
  const [trustpilotConfigured, setTrustpilotConfigured] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const cfg = await apiPost("/api/feedback/config", {});
        setPoster(cfg.poster);
        setWebpage(cfg.webpage);
        setTrustpilotConfigured(!!(cfg.trustpilot && cfg.trustpilot.configured));
      } catch (e) {
        setErr(e.message);
      }
      setLoading(false);
    })();
  }, []);
  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPost("/api/feedback/config/save", {
        poster,
        webpage
      });
      setMessage({
        type: "ok",
        text: "Saved."
      });
    } catch (e) {
      setMessage({
        type: "err",
        text: e.message
      });
    }
    setSaving(false);
  };
  if (loading) return <div style={{
    color: "#5D5D5D",
    fontSize: 13
  }}>Loading…</div>;
  if (err) return <div style={S.err}>{err}</div>;
  return <div>
      {tab === "poster" && <PosterDesignTab poster={poster} setPoster={setPoster} saving={saving} onSave={save} message={message} />}
      {tab === "webpage" && <WebpageDesignTab webpage={webpage} setWebpage={setWebpage} saving={saving} onSave={save} message={message} trustpilotConfigured={trustpilotConfigured} />}
    </div>;
}
function TrustpilotTab() {
  const [cfg, setCfg] = useState({
    businessUnitId: "",
    apiKey: "",
    apiSecret: "",
    businessUserId: "",
    locale: "en-GB",
    hasApiSecret: false,
    configured: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await apiPost("/api/feedback/config", {});
        setCfg(c => ({
          ...c,
          ...(r.trustpilot || {})
        }));
      } catch (e) {
        setMessage({
          type: "err",
          text: e.message
        });
      }
      setLoading(false);
    })();
  }, []);
  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPost("/api/feedback/config/save", {
        trustpilot: cfg
      });
      const r = await apiPost("/api/feedback/config", {});
      setCfg(c => ({
        ...c,
        apiSecret: "",
        ...(r.trustpilot || {})
      }));
      setMessage({
        type: "ok",
        text: r.trustpilot && r.trustpilot.configured ? "Trustpilot integration saved and complete." : "Saved. Complete every required field to enable invitations."
      });
    } catch (e) {
      setMessage({
        type: "err",
        text: e.message
      });
    }
    setSaving(false);
  };
  if (loading) return <div style={{
    color: "#5D5D5D",
    fontSize: 13
  }}>Loading…</div>;
  const field = (key, label, type = "text", hint = "") => <>
    <label style={S.label}>{label}</label>
    <input disabled style={S.input} type={type} value={cfg[key] || ""} placeholder={hint} onChange={e => setCfg(c => ({
      ...c,
      [key]: e.target.value
    }))} />
  </>;
  return <div style={{
    ...S.card,
    maxWidth: 600,
    opacity: 0.5
  }}>
    <div style={{
      fontWeight: 700,
      fontSize: 15,
      marginBottom: 5
    }}>Trustpilot integration</div>
    <div style={{
      fontSize: 12.5,
      color: "#5D5D5D",
      marginBottom: 10
    }}>
      Enter the details from your Trustpilot Business API application. The API secret is stored on the server and is never returned to this page.
    </div>
    <div style={{
      fontSize: 13,
      marginBottom: 12
    }}>Trustpilot integration is currently unavailable.</div>
    {field("businessUnitId", "Business Unit ID")}
    {field("apiKey", "API key (Client ID)")}
    {field("apiSecret", `API secret${cfg.hasApiSecret ? " (already saved — leave blank to keep it)" : ""}`, "password", cfg.hasApiSecret ? "••••••••••••" : "")}
    {field("businessUserId", "Business User ID")}
    {field("locale", "Invitation locale", "text", "en-GB")}
    <div style={{
      fontSize: 12,
      color: cfg.configured ? "#389E0D" : "#AD4E00",
      marginTop: 9
    }}>
      {cfg.configured ? "Configuration complete — Trustpilot invitations can be enabled in Webpage design." : "All five fields are required before invitations can be enabled."}
    </div>
    <button style={{
      ...S.btn,
      marginTop: 12
    }} onClick={save} disabled>{saving ? "Saving…" : "Save Trustpilot settings"}</button>
    {message && <div style={message.type === "err" ? S.err : S.ok}>{message.text}</div>}
  </div>;
}

// =============================================================================
// EMAILS TAB — who gets each branch's poster (RecipientsPanel, including the
// superintendent column) + the complaint reminder timer. There is no more
// separate "reply email"/"daily alert" address: every email this module
// sends — the daily awaiting-review nudge, complaint reminders, and a
// manager's reply to a customer — goes to that branch's OWN recipient,
// resolved fresh each time from RecipientsPanel's picker.
// =============================================================================
function ComplaintReminderCard({
  fill = false
}) {
  const [reminderHours, setReminderHours] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const cfg = await apiPost("/api/feedback/config", {});
        setReminderHours(cfg.reminderHours || cfg.reminderHours === 0 ? String(cfg.reminderHours) : String(DEFAULT_REMINDER_HOURS));
      } catch (e) {
        setErr(e.message);
      }
      setLoading(false);
    })();
  }, []);
  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const hrs = reminderHours === "" ? null : Number(reminderHours);
      await apiPost("/api/feedback/config/save", {
        reminderHours: hrs
      });
      setMessage({
        type: "ok",
        text: "Saved."
      });
    } catch (e) {
      setMessage({
        type: "err",
        text: e.message
      });
    }
    setSaving(false);
  };
  return <div style={{
    ...S.card,
    maxWidth: fill ? undefined : 420,
    height: fill ? "100%" : undefined,
    boxSizing: "border-box"
  }}>
      <div style={{
      fontWeight: 700,
      fontSize: 14,
      marginBottom: 8
    }}>Complaint reminders</div>
      {loading ? <div style={{
      color: "#5D5D5D",
      fontSize: 13
    }}>Loading…</div> : <>
          {err && <div style={S.err}>{err}</div>}
          <label style={S.label}>Reminder to be sent at ___ hrs after complaint received</label>
          <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8
      }}>
            <input type="number" min="0" step="1" style={{
          ...S.input,
          width: 90
        }} value={reminderHours} onChange={e => setReminderHours(e.target.value)} placeholder="48" />
            <span style={{
          fontSize: 12.5,
          color: "#5D5D5D"
        }}>hours</span>
          </div>
          <div style={{
        fontSize: 11.5,
        color: "#8C8C8C",
        marginTop: 4
      }}>
            If a complaint's outcome still isn't recorded this many hours after the branch manager was first
            emailed, the nightly scheduler sends them a further reminder. Leave blank to turn reminders off.
            The daily "awaiting review" nudge and every reminder go to that branch's own recipient above —
            there's no separate address to configure.
          </div>

          <button style={{
        ...S.btn,
        marginTop: 10
      }} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
          {message && <div style={message.type === "err" ? S.err : S.ok}>{message.text}</div>}
        </>}
    </div>;
}

// Small shared frequency picker — monthly / quarterly / yearly — used by
// both report switches below. Fires on the 1st of the relevant period
// (see feedback_reports_runner.js: isReportDay / rangeForFrequency).
function FrequencyPicker({
  value,
  onChange
}) {
  return <div style={{
    display: "flex",
    gap: 6,
    marginTop: 8
  }}>
      {["monthly", "quarterly", "yearly"].map(f => <button key={f} type="button" style={{
      ...S.btnGhost,
      flex: 1,
      padding: "6px 10px",
      background: value === f ? "#F9F0FF" : "#fff",
      borderColor: value === f ? "#722ED1" : "#D9D9D9"
    }} onClick={() => onChange(f)}>
          {f[0].toUpperCase() + f.slice(1)}
        </button>)}
    </div>;
}
const DEFAULT_REPORTS = {
  toBranches: {
    enabled: false,
    frequency: "monthly"
  },
  toEmails: {
    enabled: false,
    emails: "",
    frequency: "monthly"
  }
};

// =============================================================================
// SCHEDULED REPORTS CARD — the same Branches-tab grid/Excel, emailed
// automatically to branches and/or a fixed list of addresses.
// =============================================================================
function ReportsCard({
  fill = false
}) {
  const [reports, setReports] = useState(DEFAULT_REPORTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const cfg = await apiPost("/api/feedback/config", {});
        setReports({
          ...DEFAULT_REPORTS,
          ...(cfg.reports || {})
        });
      } catch (e) {
        setErr(e.message);
      }
      setLoading(false);
    })();
  }, []);
  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPost("/api/feedback/config/save", {
        reports
      });
      setMessage({
        type: "ok",
        text: "Saved."
      });
    } catch (e) {
      setMessage({
        type: "err",
        text: e.message
      });
    }
    setSaving(false);
  };
  const setBranchesReport = patch => setReports(r => ({
    ...r,
    toBranches: {
      ...r.toBranches,
      ...patch
    }
  }));
  const setEmailsReport = patch => setReports(r => ({
    ...r,
    toEmails: {
      ...r.toEmails,
      ...patch
    }
  }));
  return <div style={{
    ...S.card,
    maxWidth: fill ? undefined : 460,
    height: fill ? "100%" : undefined,
    boxSizing: "border-box"
  }}>
      <div style={{
      fontWeight: 700,
      fontSize: 14,
      marginBottom: 8
    }}>Scheduled reports</div>
      {loading ? <div style={{
      color: "#5D5D5D",
      fontSize: 13
    }}>Loading…</div> : <>
          {err && <div style={S.err}>{err}</div>}
          <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginBottom: 12
      }}>
            Both send the same report — organisation name, the period covered, and the grid of branch numbers
            (same as "Export to Excel" on the Branches tab) — as an Excel attachment.
          </div>

          <label style={{
        fontSize: 13,
        color: "#434343",
        display: "flex",
        alignItems: "center",
        gap: 8
      }}>
            <input type="checkbox" checked={reports.toBranches.enabled} onChange={e => setBranchesReport({
          enabled: e.target.checked
        })} />
            Send reports to branches
          </label>
          {reports.toBranches.enabled && <div style={{
        marginLeft: 24,
        marginBottom: 4
      }}>
              <div style={{
          fontSize: 11.5,
          color: "#8C8C8C",
          marginTop: 2
        }}>
                Goes to each branch's chosen recipient above.
              </div>
              <FrequencyPicker value={reports.toBranches.frequency} onChange={f => setBranchesReport({
          frequency: f
        })} />
            </div>}

          <div style={{
        borderTop: "1px solid #F0F0F0",
        margin: "14px 0 10px"
      }} />

          <label style={{
        fontSize: 13,
        color: "#434343",
        display: "flex",
        alignItems: "center",
        gap: 8
      }}>
            <input type="checkbox" checked={reports.toEmails.enabled} onChange={e => setEmailsReport({
          enabled: e.target.checked
        })} />
            Send reports to specified email addresses
          </label>
          {reports.toEmails.enabled && <div style={{
        marginLeft: 24,
        marginTop: 6
      }}>
              <label style={S.label}>Email addresses (comma separated)</label>
              <textarea style={{
          ...S.textarea,
          minHeight: 70
        }} value={reports.toEmails.emails} onChange={e => setEmailsReport({
          emails: e.target.value
        })} placeholder="area.manager@yourpharmacy.co.uk, superintendent@yourpharmacy.co.uk" />
              <FrequencyPicker value={reports.toEmails.frequency} onChange={f => setEmailsReport({
          frequency: f
        })} />
            </div>}

          <button style={{
        ...S.btn,
        marginTop: 14
      }} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
          {message && <div style={message.type === "err" ? S.err : S.ok}>{message.text}</div>}
        </>}
    </div>;
}

// =============================================================================
// STAR RATINGS TAB — two copyable, no-login public dashboard URLs: one shows
// the star rating across the whole organisation, the other breaks it down
// by branch, both graphical, both refreshing themselves every 24 hours.
// Minted lazily by the backend (/api/feedback/ratings/urls) and persisted on
// the organisation so the same two links keep working once copied out.
// =============================================================================
function CopyUrlRow({
  label,
  url
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {/* clipboard unavailable — the field itself can still be selected/copied by hand */}
  };
  return <div style={{
    marginBottom: 16
  }}>
      <label style={S.label}>{label}</label>
      <div style={{
      display: "flex",
      gap: 8
    }}>
        <input style={{
        ...S.input,
        flex: 1,
        fontFamily: "monospace",
        fontSize: 12
      }} value={url} readOnly onFocus={e => e.target.select()} />
        <button style={S.btn} onClick={copy} type="button">{copied ? "Copied!" : "Copy"}</button>
      </div>
    </div>;
}
function RatingsUrlsTab() {
  const [urls, setUrls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await apiPost("/api/feedback/ratings/urls", {});
        setUrls(r);
      } catch (e) {
        setErr(e.message);
      }
      setLoading(false);
    })();
  }, []);
  return <div style={{
    ...S.card,
    maxWidth: 640
  }}>
      <div style={{
      fontWeight: 700,
      fontSize: 14,
      marginBottom: 4
    }}>Star ratings dashboards</div>
      <div style={{
      fontSize: 12,
      color: "#5D5D5D",
      marginBottom: 14
    }}>
        Two read-only, no-login pages — copy either link to a screen, a bookmark, or anyone who
        should see it. Both are graphical and refresh themselves automatically once every 24 hours,
        so there's nothing to re-send as new ratings come in.
      </div>
      {err && <div style={S.err}>{err}</div>}
      {loading && <div style={{
      color: "#5D5D5D",
      fontSize: 13
    }}>Loading…</div>}
      {urls && <>
          <CopyUrlRow label="Organisation-wide star rating" url={urls.orgUrl} />
          <CopyUrlRow label="Star rating by branch" url={urls.branchesUrl} />
        </>}
    </div>;
}
function EmailCopiesCard({
  fill = false
}) {
  const [cfg, setCfg] = useState({
    emails: "",
    complaints: false,
    reminders: false,
    superintendent: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  useEffect(() => {
    let active = true;
    apiPost("/api/feedback/config", {}).then(r => {
      if (active) setCfg(c => ({
        ...c,
        ...r.emailCopies
      }));
    }).catch(e => {
      if (active) setMessage({
        type: "err",
        text: e.message
      });
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);
  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPost("/api/feedback/config/save", {
        emailCopies: cfg
      });
      setMessage({
        type: "ok",
        text: "Copy email settings saved."
      });
    } catch (e) {
      setMessage({
        type: "err",
        text: e.message
      });
    }
    setSaving(false);
  };
  return <div style={{
    ...S.card,
    width: fill ? "100%" : 420,
    height: fill ? "100%" : undefined,
    maxWidth: "100%",
    boxSizing: "border-box"
  }}>
    <div style={{
      fontWeight: 700,
      fontSize: 14
    }}>Copy emails</div>
    <label style={S.label}>Send copies to</label>
    <input type="text" style={S.input} value={cfg.emails} disabled={loading || saving} placeholder="name@example.com, another@example.com" onChange={e => setCfg(c => ({
      ...c,
      emails: e.target.value
    }))} />
    <div style={{
      fontSize: 12,
      color: "#5D5D5D",
      margin: "8px 0"
    }}>Separate addresses with commas. Each address receives its own copy; each send result is recorded in the Log.</div>
    {[["complaints", "Copies of complaints"], ["reminders", "Copies of reminders"], ["superintendent", "Copies of superintendent notifications"]].map(([key, label]) => <label key={key} style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
      marginTop: 10
    }}>
        <input type="checkbox" checked={cfg[key]} disabled={loading || saving} onChange={e => setCfg(c => ({
        ...c,
        [key]: e.target.checked
      }))} />{label}
      </label>)}
    <button style={{
      ...S.btn,
      marginTop: 14
    }} disabled={loading || saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
    {message && <div style={message.type === "err" ? S.err : S.ok}>{message.text}</div>}
  </div>;
}
function EmailsTab() {
  return <div>
      <RecipientsPanel />
      <div style={{
      marginTop: 16,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
      gap: 16,
      alignItems: "stretch"
    }}>
        <ComplaintReminderCard fill />
        <EmailCopiesCard fill />
        <ReportsCard fill />
      </div>
    </div>;
}
const HOW_TO_STEPS = [{
  n: 1,
  text: "Click 'Emails' to set up who receives the flyers to put up in the pharmacy advertising the service, and who is to receive alerts when messages require a response.",
  tab: "emails",
  tabLabel: "Emails"
}, {
  n: 2,
  text: "Change the default design of the poster by clicking 'Poster design' and editing the predefined text blocks and font sizes. Preview when required.",
  tab: "poster",
  tabLabel: "Poster design"
}, {
  n: 3,
  text: "Click 'Webpage design' to decide which elements you wish to have entered by the customer, and the font and colour scheme. Preview when required.",
  tab: "webpage",
  tabLabel: "Webpage design"
}, {
  n: 4,
  text: "Review and action incoming messages from 'Inbox', or see the numbers received by pharmacy in 'Branches'.",
  tab: "inbox",
  tabLabel: "Inbox"
}, {
  n: 5,
  text: "Copy a no-login link from 'Star ratings' to share a graphical, self-refreshing view of the star rating — organisation-wide or by branch.",
  tab: "ratings",
  tabLabel: "Star ratings"
}];
function HowToUsePanel({
  onJump
}) {
  return <div style={{
    marginBottom: 18
  }}>
      <div style={{
      fontSize: 12,
      fontWeight: 800,
      color: "#434343",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      marginBottom: 8
    }}>
        How to use the feedback/complaints module
      </div>
      <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: 10
    }}>
        {HOW_TO_STEPS.map(step => <div key={step.n} onClick={() => onJump(step.tab)} style={{
        ...S.card,
        cursor: "pointer",
        borderColor: "#F9F0FF",
        background: "#faf5ff"
      }} title={`Go to ${step.tabLabel}`}>
            <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 6
        }}>
              <span style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#722ED1",
            color: "#fff",
            fontSize: 11,
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}>
                {step.n}
              </span>
              <span style={{
            fontSize: 12.5,
            fontWeight: 700,
            color: "#722ED1"
          }}>{step.tabLabel}</span>
            </div>
            <div style={{
          fontSize: 12,
          color: "#434343",
          lineHeight: 1.45
        }}>{step.text}</div>
          </div>)}
      </div>
    </div>;
}

// =============================================================================
// TOP-LEVEL EXPORT
// =============================================================================
export default function PatientFeedback() {
  const [tab, setTab] = useState("inbox"); // 'inbox' | 'branches' | 'ratings' | 'emails' | 'webpage' | 'poster' | 'log'

  return <div style={S.wrap}>
      <div style={S.h1}>Patient feedback</div>
      <div style={S.sub}>Design the wall poster and the webpage it links to, then review what comes back.</div>

      <HowToUsePanel onJump={setTab} />

      <div style={S.tabs}>
        <button style={S.tabBtn(tab === "inbox")} onClick={() => setTab("inbox")}>Inbox</button>
        <button style={S.tabBtn(tab === "branches")} onClick={() => setTab("branches")}>Branches</button>
        <button style={S.tabBtn(tab === "ratings")} onClick={() => setTab("ratings")}>Star ratings</button>
        <div style={{
        flex: 1
      }} />
        <button style={S.tabBtn(tab === "emails")} onClick={() => setTab("emails")}>Emails</button>
        <button style={S.tabBtn(tab === "webpage")} onClick={() => setTab("webpage")}>Webpage design</button>
        <button style={S.tabBtn(tab === "poster")} onClick={() => setTab("poster")}>Poster design</button>
        <button style={S.tabBtn(tab === "acknowledgement")} onClick={() => setTab("acknowledgement")}>Acknowledgement text</button>
        <button style={S.tabBtn(tab === "log")} onClick={() => setTab("log")}>Log</button>
        <button style={S.tabBtn(tab === "trustpilot")} onClick={() => setTab("trustpilot")}>Trustpilot integration</button>
      </div>

      {tab === "inbox" && <InboxTab />}
      {tab === "branches" && <BranchesTab />}
      {tab === "ratings" && <RatingsUrlsTab />}
      {tab === "emails" && <EmailsTab />}
      {(tab === "poster" || tab === "webpage") && <ConfigSection tab={tab} />}
      {tab === "trustpilot" && <TrustpilotTab />}
      {tab === "acknowledgement" && <AcknowledgementTab />}
      {tab === "log" && <LogTab />}
    </div>;
}
export { FeedbackFormPreview };
